import type React from 'react';
import { IconAlertTriangle, IconCircleCheckFilled, IconClock, IconX } from '@tabler/icons-react';
import { BookingStatus } from '../interfaces/heavyDutyMachinery/IBooking';
import { PillTone } from '../theme/creamyTokens';
import { TRANSLATIONS } from './translations';

// Same icon per BookingStatus as MessageThreadPanel's own status card
// (STATUS_CARD_ICON there) -- kept as a second, independent copy rather than
// having that component import from here, since its own instances are
// rendered at per-status sizes (18/20) baked into its own JSX. Any status
// this app can be in only has one icon; if MessageThreadPanel's own mapping
// ever changes, update this one to match.
export const BOOKING_STATUS_ICON: Record<BookingStatus, React.ElementType> = {
  Pending: IconClock,
  Confirmed: IconCircleCheckFilled,
  Completed: IconCircleCheckFilled,
  Cancelled: IconX,
  HostNoShow: IconAlertTriangle,
  GuestNoShow: IconAlertTriangle,
  AwaitingQuote: IconClock,
  AwaitingPayment: IconClock,
};

// Confirmed/Completed: same positive signal MessageThreadPanel's own status
// card already gives its own teal accent (see that component's
// POSITIVE_STATUSES/statusCardStyle) -- TEAL/TEAL_SOFT_BG imported directly
// by callers rather than re-exported here, to keep this file free of a
// creamyTokens dependency it otherwise wouldn't need.
export const POSITIVE_BOOKING_STATUSES: BookingStatus[] = ['Confirmed', 'Completed'];

// AwaitingQuote/AwaitingPayment (reserva diferida) are a live negotiation,
// not a settled outcome -- deliberately neither POSITIVE nor NEGATIVE, own
// neutral/warning treatment throughout (BOOKING_STATUS_TONES below,
// MessageThreadPanel's own statusCardStyle third branch).
export const CHAT_ENABLED_STATUSES: BookingStatus[] = ['Confirmed', 'Completed', 'AwaitingQuote', 'AwaitingPayment'];

// Cancelled/no-show: MessageThreadPanel's own status card has no distinct
// color for these yet (still the same neutral treatment as Pending there --
// see its own POSITIVE_STATUSES comment). Surfaces that DO want to flag
// these as a negative outcome (e.g. the conversation list badge) can check
// this instead of inventing a color: antd's own colorError/colorErrorBg
// semantic tokens, the same danger tone already backing this app's error
// toasts (see Toast.tsx's TOAST_COLORS.error) and warning-style buttons
// (MessageThreadPanel's own "Cancelar reserva" pill uses the sibling
// colorWarning token the same way).
export const NEGATIVE_BOOKING_STATUSES: BookingStatus[] = ['Cancelled', 'HostNoShow', 'GuestNoShow'];

// Statuses whose booking has a lifecycle worth opening BookingTimelinePage
// for. Everything except Pending: a Pending booking is an unpaid checkout
// that BookingExpiryService kills within 15 minutes, so there is no progress
// to follow yet. Cancelled and the no-shows stay in -- the timeline is where
// both sides can see exactly how far the rental got before it stopped, and
// where the money ended up.
export const TIMELINE_VISIBLE_STATUSES: BookingStatus[] = [
  'Confirmed',
  'Completed',
  'Cancelled',
  'HostNoShow',
  'GuestNoShow',
  'AwaitingQuote',
  'AwaitingPayment',
];

export const BOOKING_STATUS_COLORS: Record<BookingStatus, string> = {
  Pending: 'default',
  Confirmed: 'green',
  Completed: 'blue',
  Cancelled: 'red',
  HostNoShow: 'orange',
  GuestNoShow: 'orange',
  AwaitingQuote: 'gold',
  AwaitingPayment: 'gold',
};

// Same tone vocabulary as HEAVY_DUTY_MACHINERY_SPACE_STATUS_TONES (see creamyTokens) --
// pillToneStyle turns each into a theme-derived soft-bg/strong-text pair, so
// the mobile bookings redesign's status badges stay on the app's existing
// semantic tokens instead of hardcoded colors.
export const BOOKING_STATUS_TONES: Record<BookingStatus, PillTone> = {
  Pending: 'warning',
  Confirmed: 'success',
  Completed: 'default',
  Cancelled: 'error',
  HostNoShow: 'warning',
  GuestNoShow: 'warning',
  AwaitingQuote: 'warning',
  AwaitingPayment: 'warning',
};

export const BOOKING_STATUS_LABEL_KEYS: Record<BookingStatus, string> = {
  Pending: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_PENDING,
  Confirmed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_CONFIRMED,
  Completed: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_COMPLETED,
  Cancelled: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_CANCELLED,
  HostNoShow: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_HOST_NO_SHOW,
  GuestNoShow: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_GUEST_NO_SHOW,
  AwaitingQuote: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_AWAITING_QUOTE,
  AwaitingPayment: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_STATUS_AWAITING_PAYMENT,
};
